import { lazy, type ComponentType } from 'react';
import {
  isChunkLoadError,
  tryRecoverFromChunkError,
} from '@/lib/chunk-recovery';
import { trackPageLoad } from '@/lib/page-loads';

// Wraps React.lazy so a stale-chunk error (old index.html referencing a hash
// that no longer exists after a deploy) triggers a one-time hard reload via
// the shared recovery routine. If recovery is already in progress / exhausted
// the error rethrows and AppErrorBoundary renders the fallback UI.
//
// `preload()` fetches the page's code without rendering it. The shell calls it
// for the tab bar's destinations once the first page is up, so a tab opens
// at once instead of waiting on the network. A real load is counted, so that
// preloading waits for it (see lib/page-loads).
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  const component = lazy(async () => {
    try {
      return await trackPageLoad(factory());
    } catch (err) {
      if (isChunkLoadError(err) && tryRecoverFromChunkError()) {
        // Reload is in flight — return a never-resolving promise so React
        // keeps the Suspense fallback up rather than rendering the boundary.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
  return Object.assign(component, {
    // A failed preload is not an error anyone sees: the real navigation
    // retries, and recovers from a stale chunk, through the lazy above.
    preload: () => factory().catch(() => undefined),
  });
}

// Auth pages
export const Login = lazyWithRetry(() =>
  import('./login').then((m) => ({ default: m.Login })),
);
export const ForgotPassword = lazyWithRetry(() =>
  import('./ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
);
export const ResetPassword = lazyWithRetry(() =>
  import('./ResetPassword').then((m) => ({ default: m.ResetPassword })),
);
export const PlanReview = lazyWithRetry(() =>
  import('./PlanReview').then((m) => ({ default: m.PlanReview })),
);
export const ConsentSign = lazyWithRetry(() =>
  import('./ConsentSign').then((m) => ({ default: m.ConsentSign })),
);
export const MedicalFormFill = lazyWithRetry(() =>
  import('./MedicalFormFill').then((m) => ({ default: m.MedicalFormFill })),
);
export const PlanPresentation = lazyWithRetry(() =>
  import('./PlanPresentation').then((m) => ({
    default: m.PlanPresentation,
  })),
);
export const InvitationSignup = lazyWithRetry(() =>
  import('./InvitationSignup').then((m) => ({ default: m.InvitationSignup })),
);
export const PendingInvitations = lazyWithRetry(() =>
  import('./PendingInvitations').then((m) => ({
    default: m.PendingInvitations,
  })),
);
export const VerifyEmail = lazyWithRetry(() =>
  import('./VerifyEmail').then((m) => ({ default: m.VerifyEmail })),
);
export const SelectClinic = lazyWithRetry(() =>
  import('./SelectClinic').then((m) => ({
    default: m.SelectClinic,
  })),
);
export const ClinicSetup = lazyWithRetry(() =>
  import('./setup/ClinicSetup').then((m) => ({ default: m.ClinicSetup })),
);
export const DeactivatedPage = lazyWithRetry(() =>
  import('./DeactivatedPage').then((m) => ({ default: m.DeactivatedPage })),
);

// Main pages
export const MyHome = lazyWithRetry(() =>
  import('./MyHome').then((m) => ({ default: m.MyHome })),
);
export const Patients = lazyWithRetry(() =>
  import('./Patients').then((m) => ({ default: m.Patients })),
);
export const PatientImports = lazyWithRetry(() =>
  import('./imports/PatientImports').then((m) => ({
    default: m.PatientImports,
  })),
);
export const PatientImportJob = lazyWithRetry(() =>
  import('./imports/PatientImportJob').then((m) => ({
    default: m.PatientImportJob,
  })),
);
export const IncomeImports = lazyWithRetry(() =>
  import('./imports/PatientImports').then((m) => ({
    default: m.IncomeImports,
  })),
);
export const IncomeImportJob = lazyWithRetry(() =>
  import('./imports/PatientImportJob').then((m) => ({
    default: m.IncomeImportJob,
  })),
);
export const ExpenseImports = lazyWithRetry(() =>
  import('./imports/PatientImports').then((m) => ({
    default: m.ExpenseImports,
  })),
);
export const ExpenseImportJob = lazyWithRetry(() =>
  import('./imports/PatientImportJob').then((m) => ({
    default: m.ExpenseImportJob,
  })),
);
export const PatientQrRedirect = lazyWithRetry(() =>
  import('./PatientQrRedirect').then((m) => ({ default: m.PatientQrRedirect })),
);

export const PatientProfile = lazyWithRetry(() =>
  import('./PatientProfile').then((m) => ({ default: m.PatientProfile })),
);
export const PatientLedger = lazyWithRetry(() =>
  import('./PatientLedger').then((m) => ({ default: m.PatientLedger })),
);
export const AppointmentsCalendar = lazyWithRetry(() =>
  import('./AppointmentsCalendar').then((m) => ({
    default: m.AppointmentsCalendar,
  })),
);
export const AppointmentsList = lazyWithRetry(() =>
  import('./AppointmentsList').then((m) => ({ default: m.AppointmentsList })),
);
export const PendingReservations = lazyWithRetry(() =>
  import('./PendingReservations').then((m) => ({
    default: m.PendingReservations,
  })),
);
export const Waitlist = lazyWithRetry(() =>
  import('./Waitlist').then((m) => ({ default: m.Waitlist })),
);
export const Recalls = lazyWithRetry(() =>
  import('./Recalls').then((m) => ({ default: m.Recalls })),
);
export const ReservationDetails = lazyWithRetry(() =>
  import('./ReservationDetails').then((m) => ({
    default: m.ReservationDetails,
  })),
);
export const LabRequestDetails = lazyWithRetry(() =>
  import('./LabRequestDetails').then((m) => ({ default: m.LabRequestDetails })),
);

// Lab pages
export const LabRequestsPage = lazyWithRetry(() =>
  import('./labs/LabRequestsPage').then((m) => ({
    default: m.LabRequestsPage,
  })),
);
export const LabsPage = lazyWithRetry(() =>
  import('./labs/LabsPage').then((m) => ({ default: m.LabsPage })),
);
export const LabPartnerDetailsPage = lazyWithRetry(() =>
  import('./labs/LabPartnerDetailsPage').then((m) => ({
    default: m.LabPartnerDetailsPage,
  })),
);
export const Chat = lazyWithRetry(() =>
  import('./Chat').then((m) => ({ default: m.Chat })),
);
export const ProcedureDetails = lazyWithRetry(() =>
  import('./ProcedureDetails').then((m) => ({ default: m.ProcedureDetails })),
);
export const DentalChartDemo = lazyWithRetry(() => import('./DentalChartDemo'));

// Finance pages
export const Finance = lazyWithRetry(() =>
  import('./Finance').then((m) => ({ default: m.Finance })),
);
export const FinanceIncome = lazyWithRetry(() =>
  import('./FinanceIncome').then((m) => ({ default: m.FinanceIncome })),
);
export const PlanInstalments = lazyWithRetry(() =>
  import('./PlanInstalments').then((m) => ({ default: m.PlanInstalments })),
);
export const FinanceExpenses = lazyWithRetry(() =>
  import('./FinanceExpenses').then((m) => ({ default: m.FinanceExpenses })),
);
export const FinanceExpenseDetails = lazyWithRetry(() =>
  import('./FinanceExpenseDetails').then((m) => ({
    default: m.FinanceExpenseDetails,
  })),
);
export const FinanceAssets = lazyWithRetry(() =>
  import('./FinanceAssets').then((m) => ({ default: m.FinanceAssets })),
);
export const FinanceOwnerEquity = lazyWithRetry(() =>
  import('./FinanceOwnerEquity').then((m) => ({
    default: m.FinanceOwnerEquity,
  })),
);
export const FinanceBalanceSheet = lazyWithRetry(() =>
  import('./FinanceBalanceSheet').then((m) => ({
    default: m.FinanceBalanceSheet,
  })),
);
export const FinanceCashFlow = lazyWithRetry(() =>
  import('./FinanceCashFlow').then((m) => ({
    default: m.FinanceCashFlow,
  })),
);
export const FinanceProfitLoss = lazyWithRetry(() =>
  import('./FinanceProfitLoss').then((m) => ({
    default: m.FinanceProfitLoss,
  })),
);
export const FinanceInvoiceDetails = lazyWithRetry(() =>
  import('./FinanceInvoiceDetails').then((m) => ({
    default: m.FinanceInvoiceDetails,
  })),
);
export const FinanceTransactionDetails = lazyWithRetry(() =>
  import('./FinanceTransactionDetails').then((m) => ({
    default: m.FinanceTransactionDetails,
  })),
);
export const FinanceTransactions = lazyWithRetry(() =>
  import('./FinanceTransactions').then((m) => ({
    default: m.FinanceTransactions,
  })),
);
export const FinanceMonthlyReport = lazyWithRetry(() =>
  import('./FinanceMonthlyReport').then((m) => ({
    default: m.FinanceMonthlyReport,
  })),
);
export const FinanceAccounts = lazyWithRetry(() =>
  import('./FinanceAccounts').then((m) => ({ default: m.FinanceAccounts })),
);
export const FinanceReports = lazyWithRetry(() =>
  import('./FinanceReports').then((m) => ({ default: m.FinanceReports })),
);

// Queue pages
export const ReceptionistQueue = lazyWithRetry(
  () => import('./ReceptionistQueue'),
);
export const DoctorQueueEnhanced = lazyWithRetry(
  () => import('./DoctorQueueEnhanced'),
);
export const WaitingRoomDisplay = lazyWithRetry(
  () => import('./WaitingRoomDisplay'),
);

// Analytics
export const AdvancedAnalytics = lazyWithRetry(() =>
  import('./AnalyticsDashboard').then((m) => ({
    default: m.AnalyticsDashboard,
  })),
);

export const ActivityLogPage = lazyWithRetry(() =>
  import('./ActivityLogPage').then((m) => ({ default: m.ActivityLogPage })),
);

export const ReferralSources = lazyWithRetry(() =>
  import('./ReferralSources').then((m) => ({
    default: m.ReferralSources,
  })),
);
export const PatientRiskReport = lazyWithRetry(() =>
  import('./PatientRiskReport').then((m) => ({
    default: m.PatientRiskReport,
  })),
);

// Payroll pages
export const PayrollPage = lazyWithRetry(() =>
  import('./Payroll').then((m) => ({ default: m.Payroll })),
);
export const MyEarnings = lazyWithRetry(() =>
  import('./MyEarnings').then((m) => ({ default: m.MyEarnings })),
);
export const PayrollPayoutDetails = lazyWithRetry(() =>
  import('./PayrollPayoutDetails').then((m) => ({
    default: m.PayrollPayoutDetails,
  })),
);
export const NotificationsPage = lazyWithRetry(() =>
  import('./Notifications').then((m) => ({ default: m.Notifications })),
);

// Insurance pages
export const InsuranceOverviewPage = lazyWithRetry(() =>
  import('./insurance/InsuranceOverviewPage').then((m) => ({
    default: m.InsuranceOverviewPage,
  })),
);
export const InsuranceClaimsPage = lazyWithRetry(() =>
  import('./insurance/InsuranceClaimsPage').then((m) => ({
    default: m.InsuranceClaimsPage,
  })),
);
export const ClaimDetailPage = lazyWithRetry(() =>
  import('./insurance/ClaimDetailPage').then((m) => ({
    default: m.ClaimDetailPage,
  })),
);
export const InsuranceBatchesPage = lazyWithRetry(() =>
  import('./insurance/InsuranceBatchesPage').then((m) => ({
    default: m.InsuranceBatchesPage,
  })),
);
export const InsuranceCompaniesPage = lazyWithRetry(() =>
  import('./insurance/InsuranceCompaniesPage').then((m) => ({
    default: m.InsuranceCompaniesPage,
  })),
);
export const InsuranceCompanyDetailPage = lazyWithRetry(() =>
  import('./insurance/InsuranceCompanyDetailPage').then((m) => ({
    default: m.InsuranceCompanyDetailPage,
  })),
);
export const InsurancePlanDetailPage = lazyWithRetry(() =>
  import('./insurance/InsurancePlanDetailPage').then((m) => ({
    default: m.InsurancePlanDetailPage,
  })),
);
